/**
 * Integration Adapter Types & Interfaces (Story 4.2)
 *
 * Defines the complete contract for all integration adapters.
 * Adapters are plain objects (no class hierarchy) following the codebase functional style.
 *
 * ADR-013: Each integration is an isolated module with standardized interface.
 * ADR-002: Adapters receive pre-built tenant-scoped context — never bypass tenant scoping.
 *
 * OAuth concerns (auth URLs, code exchange) are NOT part of this interface —
 * handled by @provenleap/auth in Story 4.5.
 */
import type { Platform } from "@provenleap/db";
import type { AdapterCertificationContext } from "./certification";

// ============================================================================
// Env & Context Types (AC #7)
// ============================================================================

/**
 * Narrow env interface for adapter bindings.
 * Follows DeletionEnv/DataExportEnv pattern — only what adapters actually need.
 * Adapters MUST NOT import Hono types or access full Bindings.
 */
export interface AdapterEnv {
  /** Cloudflare KV binding (typed loosely for cross-package compat) */
  readonly KV: unknown;
  /** Cloudflare D1 binding (typed loosely for cross-package compat) */
  readonly DB: unknown;
}

/**
 * Context injected into every adapter method call.
 * Adapters receive pre-scoped context — they never construct their own DB connections.
 *
 * integrationId enables ownership validation: cross-checking accountId against
 * the integration record to prevent misrouted syncs.
 */
export interface AdapterContext {
  readonly env: AdapterEnv;
  readonly tenantId: string;
  readonly clientId: string;
  readonly integrationId: string;
  /** External account identifier on the provider (e.g., GA4 property ID, Facebook page ID).
   * Populated from the integration DB row's accountId column by the route handler. */
  readonly accountId?: string;
  /**
   * Optional hint to a `fetchMetrics` implementation that prefers a specific
   * report-aggregation level (e.g. Pinterest Ads' `PIN_PROMOTION` async
   * report). Strictly additive — no current adapter populates this. Defined
   * here so adapters that consume it (Pinterest Ads from Story 19.5; future
   * hybrid adapters like Snapchat Ads) can stay strongly typed without each
   * one extending `AdapterContext` independently.
   *
   * Adapters MUST treat `undefined` as "use the default sync flow."
   */
  readonly requestedLevel?: string;
  /**
   * Optional list of metric column identifiers a caller wants surfaced. Like
   * `requestedLevel`, this is purely a hint — the adapter remains free to
   * substitute a default column list when undefined. Pinterest Ads (Story
   * 19.5) uses an overflow length to switch from sync to async report
   * templates when columns exceed the per-request 30-cap.
   */
  readonly requestedColumns?: ReadonlyArray<string>;
  /**
   * Explicit fixture switch used by demo adapters. Certification always passes
   * literal false; existing callers may omit it for the production path.
   */
  readonly fixtures?: boolean;
  /** Provider/API-sourced resource metadata from the validated grant row. */
  readonly resourceMetadata?: Readonly<Record<string, unknown>>;
  /** Provider-account identity from the validated oauth_tokens row. */
  readonly providerAccountId?: string;
  /** Opaque token-row identity needed by a small number of existing adapters. */
  readonly oauthTokenId?: string;
  /** Cooperative cancellation signal for bounded certification stages. */
  readonly signal?: AbortSignal;
  /** Live-evidence cache/scenario policy; ignored by normal product calls. */
  readonly certification?: AdapterCertificationContext;
}

/**
 * Context accepted by the live certification harness.
 *
 * The required literals make the no-fixture/live-evidence boundary visible to
 * TypeScript while leaving `AdapterContext` permissive for legacy production
 * callers and demo adapters that deliberately pass `fixtures: true`.
 */
export type CertificationAdapterContext = Omit<
  AdapterContext,
  "fixtures" | "certification"
> & {
  readonly fixtures: false;
  readonly certification: AdapterCertificationContext;
};

/**
 * Narrowed context for the cron-driven refresh path. `clientId` and
 * `integrationId` are optional because the queue dispatcher only has a token
 * row when invoking `refreshToken()` — multi-platform OAuth families like
 * Meta have N integrations per token, so neither field can be set
 * unambiguously.
 *
 * Per Discovery Item 5 (option a), this is a SEPARATE type rather than
 * widening AdapterContext; the fetch path keeps its 25+ strict-context call
 * sites intact and only `refreshToken()` accepts the wider shape.
 *
 * Adapters that read `clientId` / `integrationId` inside `refreshToken()`
 * MUST guard with null checks. Current 7 implementations do not depend on
 * either field (verified via F10 audit) — `_ctx` parameter convention is
 * common.
 */
export interface AdapterRefreshContext {
  readonly env: AdapterEnv;
  readonly tenantId: string;
  readonly clientId?: string;
  readonly integrationId?: string;
  readonly accountId?: string;
}

// ============================================================================
// Credential Types (AC #6)
// ============================================================================

/**
 * Pre-decrypted OAuth credentials passed to adapters.
 * Adapters NEVER handle token encryption/decryption — that's the caller's responsibility.
 */
export interface OAuthCredentials {
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly expiresAt: Date;
  readonly scopes: string[];
}

/**
 * Result of a token refresh operation.
 * Only returned by adapters that support token refresh (Google, Meta, LinkedIn, TikTok).
 * Shopify/Mailchimp tokens don't expire — their adapters omit refreshToken().
 */
export interface RefreshedTokens {
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly expiresAt: Date;
  /**
   * Refresh-token expiry — populated by adapters whose providers rotate the
   * refresh-token clock on each refresh (Pinterest 60d, Microsoft Ads ~90d).
   * When present, MUST be a JS Date representing a future moment (TD20 — guards
   * against epoch-seconds-vs-milliseconds footguns). When absent, the existing
   * DB value is preserved via TokenUpdateFields skip-undefined behavior.
   */
  readonly refreshTokenExpiresAt?: Date;
}

// ============================================================================
// Error Types (AC #3, #4)
// ============================================================================

/**
 * Exhaustive error codes for adapter failures.
 * Each code maps to a specific HTTP status via adapterErrorToAppError().
 */
export type AdapterErrorCode =
  | "token_expired"
  | "token_invalid"
  | "rate_limited"
  | "permission_denied"
  | "api_not_enabled"
  | "account_not_found"
  | "provider_unavailable"
  | "invalid_response"
  | "network_error"
  | "invalid_request"
  /**
   * Story 19.15 AC #6 — SB-specific gate where the customer's seller account is
   * NOT enrolled in Amazon Brand Registry. Distinct from `api_not_enabled`
   * (developer concern — LWA app re-scope) because remediation is a customer
   * BUSINESS process (enroll at amazon.com/brandregistry).
   */
  | "brand_registry_required"
  /**
   * Story 19.16 AC #6 — DSP-specific gate where the customer's Amazon account
   * is NOT enrolled in Amazon DSP (sales-rep-gated; ~5% of sponsored-ads
   * customers have DSP access). Distinct from `api_not_enabled` (LWA scope)
   * AND `permission_denied` (per-resource) AND `brand_registry_required`
   * (Brand Registry — different remediation). Lowest priority in the
   * priority-elevated unhealthy ladder: integration stays `healthy` when
   * SP+SB+SD work but DSP returns this code (partial-surface-unavailable
   * pattern carried forward from 19.15).
   */
  | "dsp_not_enabled"
  /**
   * Story 19.16.1 AC #15 — DSP customer has DSP enabled but their Amazon
   * account is NOT enrolled for the AUDIENCE report-type specifically
   * (per-report-type access gating). Subtype of `dsp_not_enabled`.
   */
  | "dsp_audience_not_enabled"
  /**
   * Story 19.16.1 AC #15 — DSP customer has DSP enabled but their Amazon
   * account is NOT provisioned for the TECHNICAL report-type. Subtype of
   * `dsp_not_enabled`.
   */
  | "dsp_technical_not_provisioned"
  /**
   * Story 19.15 AC #6 / AC #14 — multi-surface integration where AT LEAST ONE
   * surface (SP/SB/SD) is unavailable but others work. The integration row
   * remains `healthy`; this code surfaces only on the failed surface so the
   * customer's other widgets keep rendering.
   */
  | "partial_surface_unavailable";

/**
 * Structured error from adapter operations.
 * All messages are sanitized via sanitizeErrorMessage() before reaching this type.
 */
export interface AdapterError {
  readonly code: AdapterErrorCode;
  readonly message: string;
  readonly retryable: boolean;
  readonly retryAfterMs?: number;
  readonly providerStatus?: number;
  readonly providerMessage?: string;
}

/**
 * Discriminated union result type for adapter operations (AC #3).
 * Prevents uncaught exceptions across adapter boundaries.
 *
 * Usage:
 *   const result = await adapter.fetchMetrics(ctx, creds, range);
 *   if (!result.success) { handle(result.error); return; }
 *   use(result.data);
 */
export type AdapterResult<T> =
  | { readonly success: true; readonly data: T }
  | { readonly success: false; readonly error: AdapterError };

// ============================================================================
// Result Constructor Helpers
// ============================================================================

/** Create a successful AdapterResult */
export function adapterOk<T>(data: T): AdapterResult<T> {
  return { success: true, data };
}

/** Create a failed AdapterResult */
export function adapterErr<T = never>(error: AdapterError): AdapterResult<T> {
  return { success: false, error };
}

/** Type guard: checks if an AdapterResult is successful */
export function isAdapterOk<T>(
  result: AdapterResult<T>
): result is { readonly success: true; readonly data: T } {
  return result.success;
}

/** Type guard: checks if an AdapterResult is an error */
export function isAdapterErr<T>(
  result: AdapterResult<T>
): result is { readonly success: false; readonly error: AdapterError } {
  return !result.success;
}

// ============================================================================
// Metrics Types (AC #15)
// ============================================================================

/**
 * Describes a single metric an adapter can provide.
 * Used by widget rendering to know what data is available per platform.
 */
export interface MetricDefinition {
  readonly key: string;
  readonly label: string;
  readonly type: "number" | "percentage" | "duration" | "currency";
  readonly category?: string;
  /**
   * Richer data type aligned with Integration Mapping Reference. Defaults to
   * 'Integer'. `"Array"` (Story 20.2e) is the value-shape for fields whose
   * apiField returns an array — Brand Radar `links` (cited URLs),
   * `search_queries`, `tags` (per-row arrays); and `share_of_voice` on
   * `/sov-history` rows (nested `Array<{brand,value}>` per AC #1 catalogue
   * row 209). Widgets binding to these keys MUST switch on `dataType` to
   * route into list/array renderers rather than scalar KPIs.
   */
  readonly dataType?:
    | "Integer"
    | "Percentage"
    | "Currency"
    | "Duration"
    | "Ratio"
    | "Decimal"
    | "Enum"
    | "Array";
  /** Widget types this metric is compatible with. Defaults to ['*'] (all). */
  readonly compatibleWidgets?: string[];
  /** If true, metric is deprecated and should not be used in new widgets. */
  readonly deprecated?: boolean;
  /**
   * For deprecated metrics, the canonical replacement metric `key` (or `null`
   * if the metric is retired with no replacement). Story 20.2e AC #5 — Brand
   * Radar `mentions` (deprecated 2026-02-10) is replaced by `responses`;
   * `volume` (deprecated 2026-03-24) has no replacement (`null`). The widget
   * config UI surfaces this via a "deprecated — use X instead" badge.
   *
   * **Semantic note (Story 20.2e Code Review Pass 3, P3-M1):** the value is
   * the canonical replacement metric **key** (e.g.
   * `"brandRadarCitedDomainResponses"`), NOT the wire `apiField` (e.g.
   * `"responses"`). The spec's prose example uses the wire name as
   * shorthand; this contract uses the metric key because it dereferences
   * directly into a `MetricDefinition` lookup (no ambiguity when multiple
   * metrics share an apiField across endpoints — e.g. `responses` exists on
   * both /cited-domains and /cited-pages).
   */
  readonly replacedBy?: string | null;
  /**
   * Agency-facing one-line explanation surfaced in widget config UI. Especially
   * useful when `key` and `label` cannot disambiguate semantically related
   * metrics (e.g. Pinterest's `_1` paid vs `_2` earned suffix variants — the
   * label says "Earned Pin Clicks" but a description like "Clicks on
   * organically repinned copies of a paid pin — NOT billed" turns guesswork
   * into informed selection). Optional, so existing adapters are unaffected.
   */
  readonly description?: string;
  /**
   * Snake-case API field name as the provider expects it on the wire (Snapchat
   * Ads Story 19.8 Task 5.3.1). When present, the adapter's field-allowlist
   * enforcer uses this column as the single source of truth for which Snap
   * field names are valid in `requestedColumns` — preventing field-name
   * injection (Red Team Attack 5). Optional so existing adapters that don't
   * need field-allowlisting (Pinterest/MS Ads use inline column lists) are
   * unaffected.
   */
  readonly apiField?: string;
  /**
   * Story 22.2 — marks a metric as a synthetic-aggregate. Synthetic metrics
   * do NOT correspond to wire-format API fields; they are computed at
   * preset-expansion time from aggregations over time-series records or
   * persisted snapshots. The widget-config UI hides synthetic metrics from
   * per-endpoint selectors (they're exposed only via preset expansions).
   * The metric-allowlist build-time test exempts these from the per-endpoint
   * wire-field allowlist check. Mirrors the Moz Story 20.8 / Semrush 20.6 /
   * Ahrefs 20.3 synthetic-aggregate pattern.
   */
  readonly synthetic?: boolean;
  /**
   * Story 35.5 — attribution-window discriminator for Meta Marketing API
   * per-window action-count metrics. When present, signals to widget renderers
   * that this metric is part of an attribution-window family and should be
   * grouped in the picker UI (e.g. "Purchases — Choose attribution window"
   * sub-menu). `"default"` denotes the account's `default_attribution_spec`
   * window (typically `7d_click+1d_view` post Jan 12, 2026); the explicit
   * window variants are the 4 remaining active Meta windows. `7d_view` and
   * `28d_view` were removed Jan 12, 2026 and are intentionally NOT modelled.
   * Optional and meta-specific — non-Meta adapters can ignore this field.
   */
  readonly attributionWindow?: "1d_click" | "7d_click" | "28d_click" | "1d_view" | "default";
  /**
   * Story 31-31 — how this metric aggregates ACROSS ATTACHED GRANT RESOURCES
   * for the same time window (Story 31-30 multi-attachment widgets). NOT a
   * statement about aggregation across time buckets.
   * - `"additive"`: values sum (clicks, spend, sessions, followers).
   * - `"ratio"`: values are quotients; summing is WRONG. Aggregate as the
   *   weighted average Σ(value_i × w_i) / Σ(w_i) where w = the denominator
   *   component — algebraically ≡ Σnumerator / Σdenominator when
   *   value = numerator/denominator exactly.
   *
   * Distinct from `dataType: "Ratio"`, which is a VALUE-SHAPE tag (how the
   * number looks on the wire), not aggregation semantics — e.g. CPC is
   * Currency-shaped but aggregates as a ratio, while spend is Currency-shaped
   * and additive. Never derive one from the other.
   *
   * **Optional-vs-required decision (Story 31-31 AC #1):** the field stays
   * OPTIONAL at the type level; exhaustiveness across the registered catalog
   * is enforced by `metric-registry.aggregation.test.ts` instead of the
   * compiler. A required field would break every `MetricDefinition` literal
   * in test fixtures across `apps/web` (MetricSelector.test.tsx,
   * metricCompatibility.test.ts, e2e specs) and force signature changes on
   * positional builder helpers (snapchat `M(...)`, reddit catalogue) for zero
   * added safety over a CI test that reports ALL gaps in one run instead of
   * ~1,600 compile errors. Array/Enum-dataType metrics are EXEMPT and must
   * NOT carry this field (numeric-aggregation semantics don't apply).
   */
  readonly aggregationKind?: "additive" | "ratio";
  /**
   * Story 31-31 — required companion for `"ratio"` metrics WHEN the component
   * metrics exist in the same adapter's catalog. Keys are metric `key`s in
   * THIS adapter's getAvailableMetrics() (per-platform scope — never
   * cross-platform; `ctr` on google-ads refs google-ads `clicks`). Same
   * dereference rule as `replacedBy`: metric **key**, never `apiField`.
   * `denominator` is the weight Story 31-30 needs; `numerator` is optional
   * provenance (e.g. GA4 bounceRate: denominator `sessions` exists,
   * numerator "bounces" is not a catalog metric → omit).
   *
   * ⚠️ Story 31-31 Code Review: do NOT aggregate as Σnumerator/Σdenominator —
   * many ratios carry a fixed display scale (CPM/eCPM/CPP =
   * 1000 × spend/impressions; percentage rates = 100 × num/denom), so naive
   * reconstruction is off by that factor. The weighted average
   * Σ(value_i × denom_i)/Σ(denom_i) is ALWAYS exact (the scale factors out) —
   * it is the one sanctioned algorithm. Ratio
   * metrics with NO meaningful catalog weight (scores, indexes) omit
   * `ratioComponents` entirely and must be listed in the test's curated
   * `UNWEIGHTED_RATIO_ALLOWLIST` (31-30 falls back to unweighted mean).
   */
  readonly ratioComponents?: {
    readonly numerator?: string;
    readonly denominator: string;
  };
}

/**
 * Describes a single dimension an adapter can provide (Story 5.34).
 * Used by widget rendering to show available grouping dimensions per platform.
 */
export interface DimensionDefinition {
  readonly key: string;
  readonly label: string;
  readonly type:
    | "Time"
    | "Traffic"
    | "Geo"
    | "Demo"
    | "Tech"
    | "Content"
    | "Structure"
    | "Search"
    | "Links"
    | "Conversion"
    | "Delivery"
    | "Audience"
    | "Channel"
    | "Product"
    | "Review"
    | "Classification"
    | "Creative"
    | "Action";
  /** Example values for UI display (Story 5.35). */
  readonly exampleValues?: string[];
  /**
   * Set on dimensions where the provider returns delivery metrics only and
   * conversion fields will be empty (e.g. Snapchat Ads `region`/`dma`/`make`
   * breakdowns). Lets the Widget Edit Panel surface a warning when users
   * select these dimensions so they don't expect conversion data.
   * Optional so existing adapters are unaffected.
   */
  readonly deliveryOnlyWarning?: boolean;
}

/**
 * Raw metrics data returned by fetchMetrics().
 * Records are loosely typed — widgets know their own data shape.
 *
 * `warnings` is optional and surfaces non-fatal issues that produced empty
 * arrays (e.g. a Microsoft Ads shopping report on a non-shopping account, or
 * demographics blocked under the audience-size threshold). Adapters use it to
 * distinguish "the adapter ran but this report type had no data" from "the
 * adapter never ran" — without warnings these would be indistinguishable.
 * Existing adapters that do not set the field are unaffected.
 */
export interface AdapterMetrics {
  readonly platform: Platform;
  readonly accountId: string;
  readonly dateRange: { readonly start: Date; readonly end: Date };
  readonly records: Record<string, unknown>[];
  readonly fetchedAt: Date;
  readonly recordCount: number;
  readonly warnings?: ReadonlyArray<{ readonly reportType: string; readonly reason: string }>;
  /**
   * Loosely-typed bag of provider-specific metadata. Conventional keys:
   *   - `dataFreshnessLagHours` (number) — how many hours behind real-time the
   *     provider's reporting is (Story 19.14 AC #12). Widget renderer surfaces
   *     "Data refreshes ~Nh after {platform}" caption when present and > 0.
   *     Amazon Ads SP emits 1; Reddit/Snapchat/Pinterest/MS Ads/Klaviyo/
   *     Mailchimp/Shopify/Google Ads/Meta/GA4 emit 0.
   *   - `currencyCode` / `timeZone` / `dataFinalizedThrough` — Reddit Ads
   *     adapter (Story 19.11)
   *   - any other adapter-private bag values
   */
  readonly metadata?: Readonly<Record<string, unknown>>;
}

/**
 * Conventional metadata key (Story 19.14 AC #12) — number of hours the
 * provider's reporting lags real-time. Widget renderer surfaces a caption when
 * `> 0`. Adapters that emit real-time data (Reddit, Snap, Pinterest, MS Ads,
 * Klaviyo, etc.) emit 0; Amazon Ads SP emits 1.
 *
 * Defined as a constant so type-narrowing reads at call sites stay grep-able.
 */
export const ADAPTER_METADATA_KEY_DATA_FRESHNESS_LAG_HOURS =
  "dataFreshnessLagHours" as const;

/** Helper: read `dataFreshnessLagHours` from `AdapterMetrics.metadata`. */
export function readDataFreshnessLagHours(
  metrics: AdapterMetrics
): number | null {
  const v = metrics.metadata?.[ADAPTER_METADATA_KEY_DATA_FRESHNESS_LAG_HOURS];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

// ============================================================================
// Account Discovery Types (AC #14)
// ============================================================================

/**
 * Represents a selectable account/property on a platform.
 * Returned by discoverAccounts() for the integration picker UI.
 * Examples: GA4 properties, Facebook pages, YouTube channels.
 *
 * Since Story 31.4 this is also the catalog-discovery shape for the
 * two-axis multi-account model (ADR-0004) — see the "Catalog contract
 * (Epic 31)" block on {@link IntegrationAdapter.discoverAccounts} for the
 * field mapping to `oauth_grant_resources` columns.
 */
export interface AccountInfo {
  readonly id: string;
  readonly name: string;
  readonly platform: Platform;
  readonly metadata?: Record<string, unknown>;
  /**
   * OAuth scopes this specific resource needs to function (ADR-0004
   * §Per-Platform Adapter Changes). Populated per-adapter as Stories
   * 31-7..31-22 touch each one; catalog upsert defaults to `[]` when
   * absent. Display/consent metadata ONLY — never access control
   * (mirrors `oauth_tokens.scopes`).
   */
  readonly scopes?: string[];
}

/**
 * ADR-0004 vocabulary alias for {@link AccountInfo} — the catalog-discovery
 * result shape consumed by the grant-resource catalog (Stories 31-3 backfill,
 * 31-24 cron, 31-29 callbacks).
 *
 * This is a PURE type alias, not a second shape: `discoverAccounts()` IS
 * ADR-0004's `discoverResources()` (one unified contract — see the "Catalog
 * contract (Epic 31)" block on {@link IntegrationAdapter.discoverAccounts}
 * for the field mapping to `oauth_grant_resources` columns). NO structural
 * divergence is permitted — the alias is the guard against drift. Do NOT
 * introduce a separate `DiscoveredResource` interface or a parallel
 * `discoverResources()` method.
 */
export type DiscoveredResource = AccountInfo;

// ============================================================================
// Endpoint Discovery (Story 20.2 AC #19)
// ============================================================================

/**
 * Per-endpoint availability snapshot for an integration. Returned by the
 * optional `discoverEndpoints?(...)` adapter method (Story 20.2 AC #19) so
 * the future widget config UI can render per-endpoint badges (Available /
 * Subscription Required / Not Configured).
 *
 * Status meanings:
 *  - `"available"` — fully usable; widget config can call this endpoint.
 *  - `"subscription_required"` — the customer's plan doesn't include the
 *    endpoint's parent feature (e.g. Rank Tracker / Brand Radar / Web
 *    Analytics). `subscriptionRequired` names the missing feature.
 *  - `"not_configured"` — the endpoint exists but the customer hasn't yet
 *    completed a one-time setup step (e.g. Site Audit project not started).
 */
export interface EndpointDiscoveryInfo {
  readonly endpoints: ReadonlyArray<{
    readonly key: string;
    readonly status: "available" | "subscription_required" | "not_configured";
    readonly subscriptionRequired?: string;
    readonly setupHint?: string;
  }>;
}

// ============================================================================
// Rate Limit Types (AC #16)
// ============================================================================

/**
 * Per-platform rate limit configuration.
 * Declared as a readonly property on each adapter.
 * Queue consumers use this to throttle sync requests per provider.
 */
export interface RateLimitConfig {
  readonly requestsPerMinute: number;
  readonly requestsPerDay?: number;
  readonly burstLimit?: number;
  readonly retryAfterMs?: number;
}

// ============================================================================
// Integration Metadata (AC #18)
// ============================================================================

/**
 * Category classification for integration grouping in the picker UI.
 */
export type IntegrationCategory =
  | "analytics"
  | "ads"
  | "social"
  | "email"
  | "ecommerce"
  | "seo"
  | "crm"
  | "other";

/**
 * ADR-0004 Axis 1 (Story 31.5) — how an authorization grant maps to tenants on
 * the platform's side:
 *
 * - `"multi-tenant"` — one grant exposes a CATALOG of resources (ad accounts,
 *   properties, pages). Adding a new client = pick from catalog OR re-auth if
 *   the needed resource is missing. One token can back N integration rows
 *   across N clients.
 * - `"single-tenant"` — one grant = one tenant on the platform's side.
 *   Adding a new client = re-auth per client (no catalog to pick from).
 *   1:1 token-to-integration.
 *
 * The field is UNIVERSAL, not OAuth-only: api-key platforms (ahrefs, semrush,
 * moz, callrail, calltrackingmetrics, trustpilot, yelp, google-pagespeed) are
 * `"single-tenant"` by definition — one key = one account, no grant catalog.
 *
 * First consumer is Story 31-29 (OAuth callbacks branch: catalog-picker vs
 * auto-attach). Until then this is a declared classification only.
 */
export type OAuthGrantScope = "multi-tenant" | "single-tenant";

/**
 * Display-level metadata for an integration platform.
 * Separate from the data-fetching adapter — metadata exists for all 30 platforms
 * even before their adapters are implemented.
 */
export interface IntegrationMeta {
  readonly displayName: string;
  readonly category: IntegrationCategory;
  readonly description: string;
  readonly platform: Platform;
  /** OAuth provider family for grouping platforms that share a single OAuth token */
  readonly oauthFamily?: "google" | "meta" | "linkedin" | "tiktok" | "shopify" | "mailchimp" | "klaviyo" | "hubspot" | "microsoft-ads" | "pinterest-ads" | "snapchat-ads" | "reddit-ads" | "amazon-ads" | "ahrefs" | "semrush" | "moz" | "callrail" | "calltrackingmetrics" | "trustpilot" | "yelp";
  /**
   * ADR-0004 Axis 1 grant-scope classification (Story 31.5). REQUIRED, not
   * optional — because `ADAPTER_META: Record<Platform, IntegrationMeta>`, a
   * platform added without a classification is a COMPILE ERROR, not a silent
   * gap. The per-platform values (including the four documented deviations
   * from the ADR table) live in `registry.ts` with inline rationale comments.
   */
  readonly oauthGrantScope: OAuthGrantScope;
}

// ============================================================================
// Adapter Interface (AC #1, #14, #15, #16, #19)
// ============================================================================

/**
 * The complete contract for integration adapters.
 *
 * Adapters are plain objects implementing this interface — no class hierarchy.
 * Shared utilities (safeFetch, sanitizeErrorMessage) are standalone functions.
 *
 * OAuth concerns (auth URLs, code exchange) are NOT part of this interface.
 *
 * @example
 * const googleAnalytics: IntegrationAdapter = {
 *   platform: 'google-analytics-4',
 *   rateLimits: { requestsPerMinute: 100, requestsPerDay: 50000 },
 *   fetchMetrics: async (ctx, credentials, dateRange) => { ... },
 *   validateConnection: async (ctx, credentials) => { ... },
 *   discoverAccounts: async (ctx, credentials) => { ... },
 *   getAvailableMetrics: () => [ ... ],
 * };
 */
export interface IntegrationAdapter {
  readonly platform: Platform;
  readonly rateLimits: RateLimitConfig;
  /** Authentication type: 'oauth' (default) or 'api-key' for non-OAuth adapters like PageSpeed */
  readonly authType?: "oauth" | "api-key";

  /** Fetch metrics for a given date range. Returns AdapterResult to prevent uncaught exceptions. */
  fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>>;

  /** Validate the OAuth connection is still active. */
  validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>>;

  /**
   * Refresh the access token. Optional — Shopify/Mailchimp tokens don't expire.
   * Only implemented by adapters whose providers support token refresh.
   *
   * Accepts `AdapterRefreshContext` (a narrowed AdapterContext where
   * `clientId` and `integrationId` are optional) because cron-driven refresh
   * dispatch only has a token row — multi-platform OAuth families like Meta
   * have N integrations per token, so no single integrationId applies. See
   * Discovery Item 5 in tech-spec-token-lifecycle-refactor.md.
   *
   * @security This method receives `credentials.refreshToken` as PLAINTEXT.
   *   First-party adapters (in `packages/integrations/src/`) only. Third-party
   *   or plugin-loaded adapters MUST NOT be passed to the registry without a
   *   security review — the plaintext refresh token is in scope for the entire
   *   call. See TD18 in tech-spec-token-lifecycle-refactor.md.
   */
  refreshToken?(
    ctx: AdapterRefreshContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>>;

  /**
   * Discover selectable accounts/properties on the platform (AC #14).
   * Used by the integration picker UI after OAuth to let users select
   * which GA4 property, Facebook page, etc. to connect.
   *
   * ## Catalog contract (Epic 31, Story 31.4)
   *
   * This method IS ADR-0004's `discoverResources()` — ONE unified
   * discovery contract for the two-axis multi-account model. Do NOT add
   * a parallel `discoverResources()` method (two-methods-one-job drift —
   * see the OAuth integration directive's 6-place-drift history). Epic 31
   * code uses the {@link DiscoveredResource} type alias when ADR
   * vocabulary is needed.
   *
   * Field mapping to `oauth_grant_resources` columns:
   *
   * | {@link AccountInfo} field | `oauth_grant_resources` column        |
   * |---------------------------|---------------------------------------|
   * | `id`                      | `external_account_id`                 |
   * | `name`                    | `external_account_name` (column is nullable ONLY for Story 31-3 discovery-failure synthetic rows; discovery results always carry a name) |
   * | `platform`                | `platform` (part of the dedup UNIQUE index) |
   * | `metadata`                | `external_account_metadata`           |
   * | `scopes`                  | `scopes` (upsert defaults to `[]` when absent) |
   *
   * Catalog semantics:
   * - **Single-tenant platforms** return EXACTLY 1 element (the tenant
   *   itself). This applies to api-key adapters (e.g. PageSpeed) too —
   *   `discoverAccounts` is interface-required for ALL adapters.
   * - **Multi-tenant platforms** MUST return the FULL catalog — every
   *   accessible resource, paginated to completion — never just a
   *   default pick (first-page-only results poison the catalog).
   * - Implementations MUST **dedupe by `id` defensively** (ADR-0004
   *   §Anti-Patterns 4) — the DB UNIQUE index is the backstop, not the
   *   first line of defense.
   * - An **empty catalog** (healthy token, zero accessible resources) is
   *   a VALID success result: `adapterOk([])`.
   * - Results MUST contain only platform-API-sourced values: discovery
   *   output flows into the DB catalog AND into the OAuth-callback
   *   security cross-checks — it is the authorization allowlist for
   *   account attachment.
   */
  discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>>;

  /**
   * Describe what metrics this adapter can provide (AC #15).
   * Used by widget rendering to show available metrics per platform.
   * Synchronous — metric definitions are static per adapter.
   */
  getAvailableMetrics(): MetricDefinition[];

  /**
   * Describe what dimensions this adapter supports for grouping (Story 5.34).
   * Used by widget rendering to show available "Group By" options per platform.
   * Synchronous — dimension definitions are static per adapter.
   */
  getAvailableDimensions(): DimensionDefinition[];

  /**
   * Describe per-endpoint availability for an adapter that exposes more than
   * one logical endpoint (Story 20.2 AC #19).
   *
   * **Optional** — adapters that have a single endpoint (or no need to
   * surface plan-tier gating to the widget config UI) leave this `undefined`.
   *
   * Implementations typically call a free validation endpoint to read the
   * customer's subscription / plan, then map per-endpoint to one of:
   * `"available"` | `"subscription_required"` | `"not_configured"`. The
   * future widget config UI consumes this to render per-endpoint badges.
   */
  discoverEndpoints?(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<EndpointDiscoveryInfo>>;
}

// ============================================================================
// Auth Type Helpers (Story 4.11)
// ============================================================================

/** Platforms that use API keys instead of OAuth tokens */
export const API_KEY_PLATFORMS: ReadonlySet<Platform> = new Set([
  "google-pagespeed",
]);

/** Returns true if the platform uses OAuth authentication (not API key) */
export function isOAuthPlatform(platform: Platform): boolean {
  return !API_KEY_PLATFORMS.has(platform);
}
